import React from 'react';
import FormField from '../shared/FormField';
import formDataStorage from '../../utils/formDataStorage';
import { AlertTriangle, AlertCircle, Users, Building, Flag, Globe, Shield, Heart } from 'lucide-react';

const Part4Form = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('Part4Form useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('part4');
    console.log('Part4Form useEffect - 已保存数据:', savedData);
    console.log('Part4Form useEffect - 当前props数据:', data);
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('Part4Form useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]);
  // 问题列表
  const questions = [
    {
      key: 'political_org',
      label: '您是否曾经是或现在是任何政党、政治组织或政治运动的成员？',
      icon: Flag,
      iconColor: 'text-red-600'
    },
    {
      key: 'charitable_org',
      label: '您是否曾经是或现在是任何慈善组织、非政府组织或社会服务组织的成员？',
      icon: Heart,
      iconColor: 'text-green-600'
    },
    {
      key: 'professional_org',
      label: '您是否曾经是或现在是任何专业协会、工会或行业协会的成员？',
      icon: Building,
      iconColor: 'text-blue-600'
    },
    {
      key: 'religious_org',
      label: '您是否曾经是或现在是任何宗教组织、教会或宗教团体的成员？',
      icon: Globe,
      iconColor: 'text-purple-600'
    },
    {
      key: 'military_org',
      label: '您是否曾经是或现在是任何军事组织、准军事组织或民兵组织的成员？',
      icon: Shield,
      iconColor: 'text-gray-600'
    }
  ];

  // 获取问题的当前值
  const getQuestionValue = (key) => {
    const questionData = data[key];
    if (questionData && typeof questionData === 'object' && 'value' in questionData) {
      return questionData.value;
    }
    // 兼容旧的数据格式
    if (data[key] !== undefined) {
      return data[key];
    }
    // 如果数据未初始化，默认返回 false
    return false;
  };

  // 获取问题的解释文本
  const getQuestionExplanation = (key) => {
    const questionData = data[key];
    if (questionData && typeof questionData === 'object' && 'explanation' in questionData) {
      return questionData.explanation || '';
    }
    return '';
  };

  // 处理是否选择的变化
  const handleYesNoChange = (key, value) => {
    const boolValue = value === 'true';
    const currentData = data[key];
    
    // 如果当前数据是对象格式，更新value字段
    if (currentData && typeof currentData === 'object' && 'value' in currentData) {
      const newData = {
        ...currentData,
        value: boolValue,
        explanation: boolValue ? currentData.explanation : ''
      };
      onChange(key, newData);
    } else {
      // 兼容旧格式，创建新的对象格式
      const newData = {
        value: boolValue,
        explanation: ''
      };
      onChange(key, newData);
    }
  };

  // 处理解释文本的变化
  const handleExplanationChange = (key, explanation) => {
    const currentData = data[key];
    
    // 如果当前数据是对象格式，更新explanation字段
    if (currentData && typeof currentData === 'object' && 'value' in currentData) {
      const newData = {
        ...currentData,
        explanation: explanation
      };
      onChange(key, newData);
    } else {
      // 兼容旧格式，创建新的对象格式
      const newData = {
        value: currentData,
        explanation: explanation
      };
      onChange(key, newData);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">🏛️</span>
          组织和成员资格
        </h3>

        {/* 重要提示 */}
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="w-6 h-6 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-orange-800">
              <p className="font-bold mb-2 text-base">重要提示</p>
              <p className="mb-2">以下问题涉及组织和成员资格。请务必如实回答所有问题。</p>
              <p className="font-semibold">任何虚假陈述都可能导致：</p>
              <ul className="list-disc list-inside mt-1 ml-2">
                <li>签证申请被拒绝</li>
                <li>面临法律起诉</li>
                <li>被禁止入境美国</li>
              </ul>
            </div>
          </div>
        </div>

        {questions.map((question, index) => {
          const questionValue = getQuestionValue(question.key);
          const questionExplanation = getQuestionExplanation(question.key);
          const IconComponent = question.icon;
          
          return (
            <div key={question.key} className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
                  <IconComponent className={`w-5 h-5 mr-2 ${question.iconColor}`} />
                  {question.label}
                </h4>
                
                <div>
                  
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name={question.key}
                        value="false"
                        checked={questionValue === false}
                        onChange={(e) => {
                          handleYesNoChange(question.key, 'false');
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">❌ 否 - 我不是此类组织成员</span>
                    </label>
                    
                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name={question.key}
                        value="true"
                        checked={questionValue === true}
                        onChange={(e) => {
                          handleYesNoChange(question.key, 'true');
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">✅ 是 - 我是此类组织成员</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* 如果选择"是"，显示解释文本框 */}
              {questionValue === true && (
                <div className="mt-4 space-y-3">
                  {/* 强烈风险警告 */}
                  <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                    <div className="flex items-start">
                      <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                        <p className="mb-2">
                          <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                          此选项可能导致：
                        </p>
                        <ul className="list-disc list-inside ml-2 space-y-1">
                          <li className="font-semibold">面签时遭到严格审查</li>
                          <li className="font-semibold">签证申请被拒绝</li>
                          <li className="font-semibold">可能面临永久拒签</li>
                        </ul>
                        <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                          💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                        </p>
                      </div>
                    </div>
                  </div>

                  <FormField
                    label="请详细说明相关情况"
                    type="textarea"
                    value={questionExplanation}
                    onChange={(e) => handleExplanationChange(question.key, e.target.value)}
                    placeholder="请详细说明组织名称、参与时间、担任职务、活动内容等..."
                    required
                    rows={4}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* 说明信息 */}
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
          <div className="flex items-start">
            <Users className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-green-800 mb-1">说明</h4>
              <p className="text-sm text-green-700">
                参与各种组织是正常的社交活动，通常不会影响签证申请。请如实填写您的参与情况，
                包括组织名称、参与时间、担任职务等信息。如有疑问，建议咨询相关组织或专业律师。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Part4Form; 
